<template>
    <div class="">
        <b-container fluid>
            <h1>HRM Request Query</h1>

            <b-row>
              <b-col cols="12" lg="4">
                <BaseTextInput v-model="criteria.requestId" 
                  :v="$v.criteria.requestId"
                  label="Tracking or Job ID"
                  tip=""
                  slug="requestId"/>
              </b-col>
              <b-col cols="12" lg="4">
                <BaseDivisionSelector v-model="criteria.division"
                  :v="$v.criteria.division"
                  slug="division"
                  label="Division"/>
              </b-col>
              <b-col cols="12" lg="4">
                <b-form-group
                  label="Staffing Specialist" label-for="specialist-select"
                  invalid-feedback="Staffing Specialist is required">
                  <b-form-select id="specialist-select"
                         v-model="criteria.assignedTo"
                         :options="specialists"
                         value-field="userId"
                         text-field="name"/>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row class="mb-3">
                <b-col cols="12" lg="4">
                  <BaseClassificationSelector v-model="criteria.proposedClassification"
                            :v="$v.criteria.proposedClassification"
                            slug="classification"
                            label="Proposed Job Classification"
                            sr="Search for Proposed Job Classification"/>
                </b-col>

                <b-col cols="12" lg="4">
                   <BaseSelectorNoDefault v-model="criteria.resultScope" 
                        :v="$v.criteria.resultScope"
                        label="Requests to Include"
                        slug="resultsScope"
                        :options = searchScope />
                </b-col>
                <b-col cols="12" lg="4">
                  <BaseTextInput v-model="criteria.positionNumber"
                                 :v="$v.criteria.positionNumber"
                                 label="Position Number (PCN)"
                                 slug="positionNumber"/>
                </b-col>
              </b-row>
              <b-row>

                <b-col cols="12" lg="4">
                  <b-form-group label="Date Range for Assigned"
                                label-for="date-range-assigned-datepicker-start"
                                description="Date range the requests entered the Assigned Phase">
                    <label for="date-range-assigned-datepicker-start">Start Date</label>
                    <b-form-datepicker v-model="criteria.dateRangeAssignedStart"
                                       :v="$v.criteria.dateRangeAssignedStart"
                                       slug="dateRangeAssignedStart"
                                       sr="Starting date the requests entered the Assigned Phase."
                                       id="date-range-assigned-datepicker-start"
                                       reset-button/>

                    <label for="date-range-assigned-datepicker-end">End Date</label>
                    <b-form-datepicker v-model="criteria.dateRangeAssignedEnd"
                                       :v="$v.criteria.dateRangeAssignedEnd"
                                       slug="dateRangeAssignedEnd"
                                       sr="Ending date the requests entered the Assigned Phase."
                                       id="date-range-assigned-datepicker-end"
                                       :min="assignedEndMin"
                                       reset-button/>
                  </b-form-group>
                </b-col>

                <b-col cols="12" lg="4">
                  <b-form-group label="Date Range for Completed"
                                label-for="date-range-complete-datepicker-start"
                                description="Date range the requests exited the Completed Phase. If multiple Complete Phases, this uses the most recent date">
                    <label for="date-range-complete-datepicker-start">Start Date</label>
                    <b-form-datepicker v-model="criteria.dateRangeCompleteStart"
                                       :v="$v.criteria.dateRangeCompleteStart"
                                       slug="dateRangeCompleteStart"
                                       label="Date Range for Complete"
                                       sr="Starting date the requests entered the Completed Phase."
                                       id="date-range-complete-datepicker-start"
                                       reset-button/>
                    <label for="date-range-complete-datepicker-end">End Date</label>
                    <b-form-datepicker v-model="criteria.dateRangeCompleteEnd"
                                       :v="$v.criteria.dateRangeCompleteEnd"
                                       slug="dateRangeCompleteEnd"
                                       label="Date Range for Complete"
                                       sr="Ending date the requests entered the Completed Phase."
                                       id="date-range-complete-datepicker-end"
                                       :min="completeEndMin"
                                       reset-button/>

                  </b-form-group>
                </b-col>
              </b-row>
          <hr/>
              <b-row class="mb-3">
                <b-col>
                  <b-button type="button" variant="primary" @click="search">
                     <b-spinner small v-show="loading" class="mr-2"></b-spinner>Search</b-button>
                  <b-button variant="outline-primary" @click="download" class="ml-5">
                      <b-spinner small v-show="loadingDownload" class="mr-2"></b-spinner>
                      Download Spreadsheet
                  </b-button>
                </b-col>

                <b-col cols="12" lg="6" class="my-1">
                  <b-form-group
                      label="Filter"
                      label-cols-sm="3"
                      label-align-sm="right"
                      label-size="sm"
                      label-for="filterInput"
                      class="mb-0">
                    <b-input-group size="sm">
                      <b-form-input
                          v-model="filter"
                          type="search"
                          id="filterInput"
                          placeholder="Type to Search"></b-form-input>
                      <b-input-group-append>
                        <b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col>
                  <ApprovalQueue slug="search" :items="requests" :isLoading="loading" :filter="filter" @editRequest="saveCriteria"/>
                </b-col>
              </b-row>
        </b-container>
</div>
</template>

<script>
import axios from "axios"
import { required, email, minLength, maxLength, numeric, requiredUnless, requiredIf, ref, minValue, maxValue } from 'vuelidate/lib/validators'
import ApprovalQueue from '@/components/ApprovalQueue.vue'
import fileDownload  from 'js-file-download'
import SecureLS from "secure-ls";
import { commonOptions } from '@/mixins/commonOptions.js'
import pageTitle from '@/mixins/pageTitle'

export default {
  title: 'HRM Request Query',
  data: () => ({
    loading: false, 
    loadingDownload: false,
    loaded: false,

    filter: null,

    criteria: {
      division: null, 
      requestId: null,
      assignedTo: null,
      positionNumber: null,
      proposedClassification: null,
      resultScope: '1',
      completeAndCancel: false,
      workInProgress: true,

      dateRangeAssignedStart: null,
      dateRangeAssignedEnd: null,
      dateRangeCompleteStart: null,
      dateRangeCompleteEnd: null,
      assignedEnd: null,
      completedEnd: null,
    },
    requests: [],
    specialists: [],
  }),
  components: {
    ApprovalQueue
  },
  mixins: [
    commonOptions,
    pageTitle
  ],
  validations: {
    criteria: {
      division: { required: false, maxLength: maxLength(100)},
      requestId: { required: false, maxLength: maxLength(20)},
      positionNumber: { required: false },
      proposedClassification: { required: false },
      resultScope: { required: false }
    }
  },
  mounted () {
    this.fetchSpecialists()
    this.fetchSearchCriteria()
    this.fetchSearchResults()

  },
  computed: {
      dateRangeAssignedEnd: {
          get: function () {
              if (this.criteria.assignedEnd < this.criteria.dateRangeAssignedStart) {
                  return this.criteria.dateRangeAssignedStart
              }
              return this.criteria.assignedEnd
          },
          set: function (newValue) {
              this.criteria.assignedEnd = newValue
          }
      },
      assignedEndMin() {
          return this.criteria.dateRangeAssignedStart
      },
      dateRangeCompletedEnd: {
          get: function () {
              if (this.criteria.completedEnd < this.criteria.dateRangeCompleteStart) {
                  return this.criteria.dateRangeCompleteStart
              }
              return this.criteria.completedEnd
          },
          set: function (newValue) {
              this.criteria.completedEnd = newValue
          }
      },
      completeEndMin() {
          return this.criteria.dateRangeCompleteStart
      }
  },
  methods: {
    search() {
          this.loading = true
          this.loaded = false
          this.requests = []
          axios.post(`${process.env.VUE_APP_API_BASE_URL}/report/search`, this.criteria)
          .then(response => { 
            this.requests = response.data
            this.loaded = true
            this.loading = false
          })
    },
    download() {
      this.loadingDownload = true
      let requestConfig = {
        responseType: 'blob'       
      }
      
      axios.post(`${process.env.VUE_APP_API_BASE_URL}/export/search`, this.criteria, requestConfig)
      .then(response => { 
        this.loadingDownload = false
        fileDownload(response.data, 'hrm-requests.xlsx')
      })
    },

    fetchSpecialists() {
      axios.get(`${process.env.VUE_APP_API_BASE_URL}/organization/specialist`)
        .then(response => {
          this.specialists = response.data;
          const emptySpecialist = {
            name: "-- Please select an option --"
          }
          this.specialists.unshift(emptySpecialist)
      });
    },

    fetchSearchResults(){
      var ls = new SecureLS({ isCompression: false });
      var results = ls.get('hrm-request-search-results')
      if(results){
        this.requests = results
      }
    },

    fetchSearchCriteria(){
      var ls = new SecureLS({ isCompression: false });
      var criteria = ls.get('hrm-request-search-criteria')
      if(criteria){
        this.criteria = criteria
        if (!this.criteria.resultScope) {
          this.criteria.resultScope = '1'
        }
      }
    },
    saveCriteria(){
      var ls = new SecureLS({ isCompression: false });
      ls.set('hrm-request-search-criteria', this.criteria)
    },
  }
 }
</script>

<style>

</style>
